import { Injectable } from '@angular/core';
import { FilingFormState } from './filing-form-state.service';
import { SheetValidator } from './sheet-validator';
import { role0FilingPrerequisitesValidator } from './validators/role0-filing-prerequisites.validator';
import { role1FilingInfoValidator } from './validators/role1-filing-info.validator';
import { role2ScopeOfFilingValidator } from './validators/role2-scope-of-filing.validator';
import { role3StockExchangeValidator } from './validators/role3-stock-exchange.validator';
import { role4DirectorsReportValidator } from './validators/role4-directors-report.validator';
import { role5StatementByDirectorsValidator } from './validators/role5-statement-by-directors.validator';
import { role6DirectorBusinessReviewValidator } from './validators/role6-director-business-review.validator';
import { role7AuditorsReportValidator } from './validators/role7-auditors-report.validator';
import { role8StatementOfFinancialPositionValidator } from './validators/role8-statement-of-financial-position.validator';
import { role9SubClassificationValidator } from './validators/role9-sub-classification.validator';
import { role10SofpLiquidityValidator } from './validators/role10-sofp-liquidity.validator';
import { role11SubClassificationLiquidityValidator } from './validators/role11-sub-classification-liquidity.validator';
import { role12StatementOfProfitOrLossValidator } from './validators/role12-statement-of-profit-or-loss.validator';
import { role13AnalysisOfProfitOrLossValidator } from './validators/role13-analysis-of-profit-or-loss.validator';
import { role14SoplByNatureValidator } from './validators/role14-sopl-by-nature.validator';
import { role15AnalysisOfProfitOrLossByNatureValidator } from './validators/role15-analysis-of-profit-or-loss-by-nature.validator';
import { role15StatementOfComprehensiveIncomeValidator } from './validators/role15-statement-of-comprehensive-income.validator';
import { role16SociBeforeTaxValidator } from './validators/role16-soci-before-tax.validator';
import { role17CashFlowsDirectValidator } from './validators/role17-cash-flows-direct.validator';
import { role18CashFlowsIndirectValidator } from './validators/role18-cash-flows-indirect.validator';
import { role19StatementOfChangesInEquityValidator } from './validators/role19-statement-of-changes-in-equity.validator';
import { role20NotesCorporateInformationValidator } from './validators/role20-notes-corporate-information.validator';
import { role21NotesAccountingPoliciesValidator } from './validators/role21-notes-accounting-policies.validator';
import { role22NotesListOfNotesValidator } from './validators/role22-notes-list-of-notes.validator';
import { role23NotesIssuedCapitalValidator } from './validators/role23-notes-issued-capital.validator';
import { role24NotesRelatedPartyTransactionsValidator } from './validators/role24-notes-related-party-transactions.validator';

/**
 * PureValidatorRegistry
 *
 * Central registry of pure SheetValidators keyed by sheet key. The
 * ConversionValidatorService orchestrator queries this registry per sheet
 * during cache-miss compute:
 *
 *   - Hit  -> run the pure validator against the loaded payload.
 *   - Miss -> fall back to the existing mount-based path for that sheet
 *             (the path that lives in runBackgroundValidationCycle and the
 *             role component's own validation methods).
 *
 * This per-sheet fallback is what allows Phase 2 to ship sheet-by-sheet
 * without an all-or-nothing cutover. Performance improves continuously as
 * sheets get registered; once all 24 are registered, Phase 2F deletes the
 * mount-based path entirely.
 *
 * Validators self-register by calling `register()` from their module-level
 * factory (see `validators/index.ts` once Tier 1 lifts begin). Registration
 * is one-shot — duplicate registrations throw to surface accidental
 * double-registration during development.
 *
 * The registry holds plain references; it does NOT own validator lifetime
 * since SheetValidators are stateless. Tests can pass alternate registries
 * by providing a stub at the injector level (overrideProvider in TestBed).
 */
@Injectable({ providedIn: 'root' })
export class PureValidatorRegistry {
  private readonly validators = new Map<keyof FilingFormState, SheetValidator>();

  /**
   * Phase 2 lift roster. Each entry is a fully-pure SheetValidator that
   * replaces (and is parity-checked against) the corresponding role
   * component's mount-based validation. Sheets are added one at a time
   * across Tiers 1-4; once all 26 keys are covered, the orchestrator's
   * coverage gate flips to 'computed' and the editor's
   * runBackgroundValidationCycle becomes deletable in Phase 2F.
   *
   * Keeping this as a flat array (instead of dynamic discovery) makes the
   * roster trivially auditable in code review and prevents accidental
   * registration via stray imports.
   */
  private readonly LIFTED_VALIDATORS: ReadonlyArray<SheetValidator> = [
    role0FilingPrerequisitesValidator,
    role1FilingInfoValidator,
    role2ScopeOfFilingValidator,
    role3StockExchangeValidator,
    role4DirectorsReportValidator,
    role5StatementByDirectorsValidator,
    role6DirectorBusinessReviewValidator,
    role7AuditorsReportValidator,
    role8StatementOfFinancialPositionValidator,
    role9SubClassificationValidator,
    role10SofpLiquidityValidator,
    role11SubClassificationLiquidityValidator,
    role12StatementOfProfitOrLossValidator,
    role13AnalysisOfProfitOrLossValidator,
    role14SoplByNatureValidator,
    role15AnalysisOfProfitOrLossByNatureValidator,
    role15StatementOfComprehensiveIncomeValidator,
    role16SociBeforeTaxValidator,
    role17CashFlowsDirectValidator,
    role18CashFlowsIndirectValidator,
    role19StatementOfChangesInEquityValidator,
    role20NotesCorporateInformationValidator,
    role21NotesAccountingPoliciesValidator,
    role22NotesListOfNotesValidator,
    role23NotesIssuedCapitalValidator,
    role24NotesRelatedPartyTransactionsValidator,
  ];

  constructor() {
    for (const validator of this.LIFTED_VALIDATORS) {
      this.register(validator);
    }
  }

  /**
   * Register a pure validator for its declared sheet key.
   *
   * Throws if a validator is already registered for the same sheet — in
   * Phase 2 each sheet has exactly one canonical validator, and a
   * collision usually means two modules accidentally registered the same
   * one (e.g. import side-effects firing twice).
   */
  register(validator: SheetValidator): void {
    const existing = this.validators.get(validator.sheetKey);
    if (existing) {
      throw new Error(
        `[PureValidatorRegistry] duplicate validator for sheet "${String(validator.sheetKey)}"`
      );
    }
    this.validators.set(validator.sheetKey, validator);
  }

  /**
   * Look up a validator by sheet key. Returns undefined when no pure
   * validator has been lifted for that sheet yet — caller should fall
   * back to the mount-based path.
   */
  get(sheetKey: keyof FilingFormState): SheetValidator | undefined {
    return this.validators.get(sheetKey);
  }

  /**
   * Set of sheet keys covered by registered validators. Used by the
   * orchestrator to decide, per conversion, which sheets can use the
   * pure path and which must fall back. Returns a snapshot — caller may
   * not mutate.
   */
  registeredSheets(): ReadonlySet<keyof FilingFormState> {
    return new Set(this.validators.keys());
  }
}
